import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BookmarkGroupData } from "@/routes/bookmarks/types/bookmarkGroupData";
import { Bookmark } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useFetcher } from "react-router-dom";

interface BookmarkModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookmarkContents: BookmarkGroupData[];
}

export function BookmarkModal({
  isOpen,
  onClose,
  bookmarkContents,
}: BookmarkModalProps) {
  const { t } = useTranslation();
  const fetcher = useFetcher();
  const [selectedBookmarkGroup, setSelectedBookmarkGroup] =
    useState<string>("");
  useEffect(() => {
    if (fetcher.data?.success) {
      onClose();
      setSelectedBookmarkGroup("");
    }
  }, [fetcher.data]);
  if (!isOpen) return null;
  if (bookmarkContents.length > 0)
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("lecture.bookmark.title")}</DialogTitle>
          </DialogHeader>
          <fetcher.Form method="post" className="space-y-4">
            <input type="hidden" name="intent" value="addBookmark" />
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                {t("lecture.bookmark.selectGroup")}
              </label>
              <Select
                value={selectedBookmarkGroup}
                onValueChange={setSelectedBookmarkGroup}
                name="bookmarkGroupId"
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("lecture.bookmark.chooseGroup")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {bookmarkContents.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  onClose();
                  setSelectedBookmarkGroup("");
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={!selectedBookmarkGroup}>
                <>
                  <Bookmark className="w-4 h-4 mr-2" />
                  {t("lecture.bookmark.addBookmark")}
                </>
              </Button>
            </div>
          </fetcher.Form>
        </DialogContent>
      </Dialog>
    );
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("lecture.bookmark.title")}</DialogTitle>
        </DialogHeader>
        <Button>
          <Link to="/bookmarks">
            {t("lecture.bookmark.noGroups")}
          </Link>
        </Button>
      </DialogContent>
    </Dialog>
  );
}
